import { create } from "zustand";
import {
  PDA1120_R01Dto,
  PDA1120_R01SearchParams,
  FeederInfoDto,
  MaterialCheckResultDto,
  MaterialCheckItem,
} from "src/containers/PDA/PDA1120_R01/PDA1120_R01Dto";
import { toLocalDateTime } from "src/utils/string/stringUtil";

export interface PDA1120_R01Store {
  orderList: PDA1120_R01Dto[];
  selectedOrder: PDA1120_R01Dto | null;
  feederList: FeederInfoDto[];
  loading: boolean;
  searchParams: PDA1120_R01SearchParams;
  selectedFeeder: FeederInfoDto | null;
  barcode: string;
  scannedLot: MaterialCheckResultDto | null;
  machineBarcode: string;
  machineFeederId: string;
  checkList: MaterialCheckItem[];
  localCheckedQty: Record<string, number>; // key: `${feederCd}_${itemCd}`

  setOrderList: (orderList: PDA1120_R01Dto[]) => void;
  setSelectedOrder: (order: PDA1120_R01Dto | null) => void;
  setFeederList: (feederList: FeederInfoDto[]) => void;
  setSelectedFeeder: (feeder: FeederInfoDto | null) => void;
  updateFeederCheckStatus: (coordinateCd: string, lotNo: string, lotQty: number) => void;
  setBarcode: (barcode: string) => void;
  setScannedLot: (scannedLot: MaterialCheckResultDto | null) => void;
  resetScanForm: () => void;
  setMachineBarcode: (machineBarcode: string) => void;
  setMachineFeederId: (machineFeederId: string) => void;
  resetMachineScan: () => void;
  setLoading: (loading: boolean) => void;
  setSearchParams: (searchParams: PDA1120_R01SearchParams) => void;
  updateSearchParam: (field: keyof PDA1120_R01SearchParams, value: string) => void;
  resetSearchParams: () => void;
  reset: () => void;
  addToCheckList: (item: MaterialCheckItem) => void;
  removeFromCheckList: (lotNo: string) => void;
  clearCheckList: () => void;
  updateLocalCheckedQty: () => void;
}

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

const initialSearchParams: PDA1120_R01SearchParams = {
  productWorkNo: "",
  searchText: "",
  startDate: formatLocalDate(firstDay),
  endDate: formatLocalDate(today),
};

const usePDA1120_R01Store = create<PDA1120_R01Store>((set) => ({
  orderList: [],
  selectedOrder: null,
  feederList: [],
  loading: false,
  searchParams: initialSearchParams,
  selectedFeeder: null,
  barcode: "",
  scannedLot: null,
  machineBarcode: "",
  machineFeederId: "",
  checkList: [],
  localCheckedQty: {},

  setOrderList: (orderList) => set({ orderList }),
  setSelectedOrder: (order) =>
    set({
      selectedOrder: order,
      feederList: [],
      selectedFeeder: null,
      barcode: "",
      scannedLot: null,
      machineBarcode: "",
      machineFeederId: "",
      checkList: [],
      localCheckedQty: {},
    }),

  setFeederList: (feederList) => set({ feederList }),
  setSelectedFeeder: (feeder) =>
    set({
      selectedFeeder: feeder,
      barcode: "",
      scannedLot: null,
      machineBarcode: "",
      machineFeederId: "",
    }),

  updateFeederCheckStatus: (coordinateCd, lotNo, lotQty) =>
    set((state) => ({
      feederList: state.feederList.map((feeder) =>
        feeder.coordinateCd === coordinateCd
          ? { ...feeder, checkStatus: "Y", lotNo, lotQty }
          : feeder
      ),
    })),

  setBarcode: (barcode) => set({ barcode }),
  setScannedLot: (scannedLot) => set({ scannedLot }),
  resetScanForm: () => set({ barcode: "", scannedLot: null }),

  setMachineBarcode: (machineBarcode) => set({ machineBarcode }),
  setMachineFeederId: (machineFeederId) => set({ machineFeederId }),
  resetMachineScan: () => set({ machineBarcode: "", machineFeederId: "" }),

  setLoading: (loading) => set({ loading }),
  setSearchParams: (searchParams) => set({ searchParams }),
  updateSearchParam: (field, value) =>
    set((state) => ({
      searchParams: { ...state.searchParams, [field]: value },
    })),
  resetSearchParams: () => set({ searchParams: initialSearchParams }),
  reset: () =>
    set({
      orderList: [],
      selectedOrder: null,
      feederList: [],
      loading: false,
      searchParams: initialSearchParams,
      selectedFeeder: null,
      barcode: "",
      scannedLot: null,
      machineBarcode: "",
      machineFeederId: "",
      checkList: [],
      localCheckedQty: {},
    }),

  //  복합키: feederCd + itemCd
  addToCheckList: (item) =>
    set((state) => {
      // 실장 목록에 이미 해당 LOT/피더가 있는지 확인
      if (
        state.checkList.some(
          (check) =>
            check.lotNo === item.lotNo &&
          // 피더 코드 비교
            String(check.feederCd ?? "").trim() === String(item.feederCd ?? "").trim()
        )
      ) {
        // 이미 추가된 LOT/피더 메시지 출력
        console.warn("이미 추가된 LOT/피더:", item.lotNo, item.feederCd);
        return state;
      }
      const newCheckList = [
        ...state.checkList,
        { ...item, scannedAt: toLocalDateTime(new Date()) },
      ];
      const newLocalCheckedQty = { ...state.localCheckedQty };
      const key = `${String(item.feederCd ?? "").trim()}_${item.itemCd}`;
      newLocalCheckedQty[key] = (newLocalCheckedQty[key] || 0) + Number(item.lotQty);
      return { checkList: newCheckList, localCheckedQty: newLocalCheckedQty };
    }),

  removeFromCheckList: (lotNo) =>
    set((state) => {
      // 실장 목록에서 해당 LOT 삭제
      const removedItems = state.checkList.filter((item) => item.lotNo === lotNo);
      if (removedItems.length === 0) return state;
      // 실장 목록에서 해당 LOT 삭제
      const newCheckList = state.checkList.filter((item) => item.lotNo !== lotNo);
      // 로컬 실장량 조회
      const newLocalCheckedQty = { ...state.localCheckedQty };
      // 로컬 실장량 삭제
      removedItems.forEach((removed) => {
        const key = `${String(removed.feederCd ?? "").trim()}_${removed.itemCd}`;
        // 로컬 실장량 삭제
        newLocalCheckedQty[key] = (newLocalCheckedQty[key] || 0) - Number(removed.lotQty);
        // 로컬 실장량이 0보다 작으면 삭제
        if (newLocalCheckedQty[key] <= 0) delete newLocalCheckedQty[key];
      });
      return { checkList: newCheckList, localCheckedQty: newLocalCheckedQty };
    }),

  clearCheckList: () => set({ checkList: [], localCheckedQty: {} }),

  updateLocalCheckedQty: () =>
    set((state) => {
      const newQty: Record<string, number> = {};
      state.checkList.forEach((item) => {
        const key = `${String(item.feederCd ?? "").trim()}_${item.itemCd}`;
        newQty[key] = (newQty[key] || 0) + Number(item.lotQty);
      });
      return { localCheckedQty: newQty };
    }),
}));

export default usePDA1120_R01Store;
